import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Lupe über dem Produkt-Querschnitt.
 *
 * Zeigt einen vergrößerten Ausschnitt des Bildes unter dem Zeiger. Das Ein- und
 * Ausblenden macht CSS (`:hover`), diese Komponente positioniert nur.
 *
 * Bewusst anders als im Prototyp:
 * - Der Radius wird aus der tatsächlichen Elementbreite gelesen statt als
 *   Konstante gepflegt. Im Prototyp stand `LW = 97.5` neben `width: 195px` —
 *   zwei Stellen, die auseinanderlaufen können.
 * - Die Bildquelle wird einmal gesetzt statt bei jeder Mausbewegung, und sie
 *   kommt aus `data-zoom-src`. Über `src` auszulesen träfe bei `srcset` die
 *   Fallback-Auflösung, also ein unnötig unscharfes Lupenbild.
 *
 * @typedef {object} CutawayRefs
 * @property {HTMLElement} stage - Die Bühne, in der die Lupe positioniert wird.
 * @property {HTMLElement} [lens] - Die Lupe. Fehlt, wenn sie abgeschaltet ist.
 *
 * @extends {Component<CutawayRefs>}
 */
export class CutawayFigure extends Component {
  requiredRefs = ['stage'];

  #image = null;
  #onPointerMove = null;

  connectedCallback() {
    super.connectedCallback();

    const { stage, lens } = this.refs;
    if (!lens) return;

    // Ohne Zeigegerät gibt es kein Hover — dann bleibt die Lupe aus.
    // Das deckt sich mit `@media (hover: none)` im Stylesheet.
    if (!matchMedia('(hover: hover)').matches) return;

    this.#image = this.querySelector('.cutaway__image');
    if (!this.#image) return;

    const src = this.dataset.zoomSrc;
    if (src) lens.style.backgroundImage = `url("${CSS.escape(src).replace(/\\/g, '')}")`;

    this.#onPointerMove = this.#handlePointerMove.bind(this);
    stage.addEventListener('pointermove', this.#onPointerMove, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    if (this.#onPointerMove) {
      this.refs.stage?.removeEventListener('pointermove', this.#onPointerMove);
      this.#onPointerMove = null;
    }
  }

  /**
   * Positioniert die Lupe auf dem Zeiger und verschiebt ihren Bildausschnitt so,
   * dass der Punkt unter dem Zeiger in der Lupenmitte liegt.
   *
   * @param {PointerEvent} event
   */
  #handlePointerMove(event) {
    const { stage, lens } = this.refs;
    if (!lens || !this.#image) return;

    const zoom = Number(getComputedStyle(this).getPropertyValue('--cutaway-zoom')) || 2.1;
    const radius = lens.offsetWidth / 2;

    const imageRect = this.#image.getBoundingClientRect();
    const stageRect = stage.getBoundingClientRect();

    // Zeigerposition relativ zum Bild — enthält die optische Zentrierkorrektur,
    // weil sie im gemessenen Rechteck bereits steckt.
    const x = event.clientX - imageRect.left;
    const y = event.clientY - imageRect.top;

    lens.style.left = `${event.clientX - stageRect.left - radius}px`;
    lens.style.top = `${event.clientY - stageRect.top - radius}px`;
    lens.style.backgroundSize = `${imageRect.width * zoom}px ${imageRect.height * zoom}px`;
    lens.style.backgroundPosition = `${-(x * zoom - radius)}px ${-(y * zoom - radius)}px`;
  }
}

if (!customElements.get('cutaway-figure')) {
  customElements.define('cutaway-figure', CutawayFigure);
}

// prefersReducedMotion wird hier nicht zum Abschalten gebraucht — die Lupe folgt
// dem Zeiger ohne Eigenbewegung. Die Einblend-Transition schaltet das Stylesheet ab.
export { prefersReducedMotion };
